<template>
  <div :class="[$attrs.class, rootClasses]" :style="$attrs.style">
    <p
      v-if="description"
      class="body-regular-small w-input-description"
      :data-testid="sectionTag ? `${sectionTag}-description` : undefined"
      :style="{ color: tokens.color.input.text.description }"
    >
      {{ description }}
    </p>
    <v-text-field
      v-if="usesTextField"
      ref="textFieldRef"
      v-model="model"
      v-maska="textMask"
      :data-testid="sectionTag"
      v-bind="fieldAttrs"
      :label="getLabel"
      :rules="mergedRules"
      :base-color="tokens.color.input.border.default"
      :color="tokens.color.input.border.default"
    >
      <!-- Vuetify renders the label itself, so it carries no data-testid, leaving the label reachable
           only by CSS/text matching. Own the slot to attach a section-scoped test id using the
           shared Element suffix. Content is unchanged, so floating-label behaviour is unaffected. -->
      <template v-if="getLabel" #label>
        <span :data-testid="sectionTag ? `${sectionTag}-${Element.LABEL}` : undefined">{{ getLabel }}</span>
      </template>
      <template v-if="$slots['input-content']" #default="slotData">
        <slot name="input-content" v-bind="slotData || {}"></slot>
      </template>
      <template #message="{ message }">
        <slot name="message-content" :message="message">
          <v-icon
            :data-testid="sectionTag ? `${sectionTag}-error-icon` : undefined"
            :icon="mdiAlert"
            class="input-message-icon"
            :style="{ color: tokens.color.input.icon.error }"
          ></v-icon>
          <span
            :data-testid="sectionTag ? `${sectionTag}-error-message` : undefined"
            class="label-semibold-small pa-0"
            :style="{ color: tokens.color.input.text.error }"
          >
            {{ message }}
          </span>
        </slot>
      </template>
      <template #append-inner="{}">
        <slot name="append-inner">
          <WHelpTooltip
            v-if="helperText"
            :section-tag="sectionTag"
            :description="helperText"
            :width="helperWidth"
            position="top"
          >
            <template #icon>
              <IconQuestion :section-tag="`${sectionTag}-tooltip-question-icon`" />
            </template>
          </WHelpTooltip>
        </slot>
      </template>
      <template #prepend-inner="">
        <v-icon
          v-if="search"
          :data-testid="sectionTag ? `${sectionTag}-search-icon` : undefined"
          :icon="mdiMagnify"
          size="small"
          :class="searchIconClasses"
        />
        <span v-if="prefix" class="w-input-prefix body-regular-small">{{ prefix }}</span>
        <slot name="prepend-inner"></slot>
      </template>
    </v-text-field>
    <v-textarea
      v-else
      ref="textAreaRef"
      v-model="model"
      v-bind="fieldAttrs"
      :label="getLabel"
      :rules="mergedRules"
      :base-color="tokens.color.input.border.default"
      :color="tokens.color.input.border.default"
      :rows="props.rows"
      :auto-grow="true"
      persistent-counter
      :data-testid="sectionTag"
      :class="{ 'no-label': noLabel }"
      @keydown.enter="onEnterKey"
    >
      <template v-if="getLabel" #label>
        <span :data-testid="sectionTag ? `${sectionTag}-${Element.LABEL}` : undefined">{{ getLabel }}</span>
      </template>
      <template #message="{ message }">
        <slot name="message-content" :message="message">
          <v-icon
            :data-testid="sectionTag ? `${sectionTag}-error-icon` : undefined"
            :icon="mdiAlert"
            class="input-message-icon"
            :style="{ color: tokens.color.input.icon.error }"
          ></v-icon>
          <span
            :data-testid="sectionTag ? `${sectionTag}-error-message` : undefined"
            class="label-semibold-small pa-0"
            :style="{ color: tokens.color.input.text.error }"
          >
            {{ message }}
          </span>
        </slot>
      </template>
      <template v-if="$attrs.counter" #counter="{ value, max }">
        <span :data-testid="sectionTag ? `${sectionTag}-counter` : undefined"> {{ value }}/{{ max }} </span>
      </template>
      <template #append-inner="{}">
        <slot name="append-inner">
          <WHelpTooltip
            v-if="helperText"
            :section-tag="sectionTag"
            :description="helperText"
            :width="helperWidth"
            position="top"
          >
            <template #icon>
              <IconQuestion :section-tag="`${sectionTag}-tooltip-question-icon`" />
            </template>
          </WHelpTooltip>
        </slot>
      </template>
      <template #prepend-inner="">
        <v-icon
          v-if="search"
          :data-testid="sectionTag ? `${sectionTag}-search-icon` : undefined"
          :icon="mdiMagnify"
          size="small"
          :class="searchIconClasses"
        />
        <span v-if="prefix" class="w-input-prefix body-regular-small">{{ prefix }}</span>
        <slot name="prepend-inner"></slot>
      </template>
    </v-textarea>
  </div>
</template>

<script setup lang="ts">
defineOptions({ inheritAttrs: false })

import { tokens } from '@/plugins/vuetify'
import { computed, ref, useAttrs, useSlots, watch } from 'vue'
import { mdiAlert, mdiMagnify } from '@mdi/js'
import { WHelpTooltip } from '@wasabi/data-visualization'
import { IconQuestion } from '@wasabi/patterns'
import { $t } from '@/plugins/i18n'
import { Element } from '@/constants/selectors/shared'

export interface WInputFieldProps {
  sectionTag?: string
  /** Optional: the component's own `noLabel` mode is derived from its absence. */
  label?: string
  required?: boolean
  floatingOffset?: string
  description?: string
  helperText?: string
  helperWidth?: string
  mask?: string
  rows?: number
  prefix?: string
  allow?: string
  search?: boolean
  embedded?: boolean
  inputContentActive?: boolean
}

const props = withDefaults(defineProps<WInputFieldProps>(), {
  sectionTag: 'unspecified',
  required: false,
  floatingOffset: '0px',
  description: '',
  helperText: '',
  helperWidth: 'auto',
  mask: '',
  rows: 1,
  prefix: '',
  allow: '',
  search: false,
  embedded: false,
  inputContentActive: false
})

const model = defineModel<string>({ default: '' })

/** When `allow` is set, strip characters not matching the pattern */
watch(model, (val) => {
  if (props.allow && val) {
    const regex = new RegExp(props.allow, 'g')
    const filtered = (val.match(regex) || []).join('')
    if (filtered !== val) {
      model.value = filtered
    }
  }
})

const attrs = useAttrs()
const slots = useSlots()

const fieldAttrs = computed(() => {
  const { class: _cls, style: _style, ...rest } = attrs
  return {
    'hide-details': 'auto',
    ...rest
  }
})

const getLabel = computed(() => {
  if (!props.label) return undefined
  return props.required ? props.label + '*' : props.label
})

const mergedRules = computed(() => {
  const externalRules = (attrs.rules as ((v: any) => any)[]) || []
  if (!props.required) return externalRules
  const requiredRule = (v: any) => !!v || $t('app.validation.required', { field: props.label })
  return [requiredRule, ...externalRules]
})

const maxRows = computed((): number => {
  const value = attrs['max-rows'] as string | number | undefined
  const parsed = value ? Number(value) : 1
  return isNaN(parsed) ? 1 : parsed
})

const isMultiline = computed(() => props.rows > 1 || maxRows.value > 1)

const noLabel = computed((): boolean => {
  return !props.label && maxRows.value === 1
})

const usesTextField = computed(() => Boolean(props.mask) || props.embedded || Boolean(slots['input-content']))
const textMask = computed(() => props.mask || undefined)

const rootClasses = computed(() => ({
  'w-input-field--embedded': props.embedded,
  'w-input-field--with-input-content': Boolean(slots['input-content']),
  'w-input-field--with-active-input-content': Boolean(slots['input-content']) && props.inputContentActive
}))

const searchIconClasses = computed(() => ({
  'w-input-field__search-icon': true,
  'me-1': !props.embedded
}))

const textFieldRef = ref<HTMLElement | null>(null)
const textAreaRef = ref<HTMLElement | null>(null)
const onEnterKey = (event: KeyboardEvent) => {
  if (!isMultiline.value) {
    event.preventDefault()
  }
}

const focus = () => {
  if (usesTextField.value) {
    textFieldRef.value?.focus()
  } else {
    textAreaRef.value?.focus()
  }
}

defineExpose({ focus })
</script>

<style scoped lang="scss">
@include wasabi-outlined-field;

:deep(.v-field__append-inner) {
  .v-icon {
    opacity: 1 !important;
  }
}

:deep(.v-field--variant-outlined .v-label.v-field-label--floating) {
  margin-left: v-bind('$props.floatingOffset') !important;
}

:deep(.v-input__details) {
  padding: 6px 0 0;
}

:deep(.v-field--error) {
  &:not(.v-field--focused) {
    .v-field__outline {
      color: v-bind('tokens.color.input.text.error') !important;
    }
  }
}

:deep(.v-input--error .v-field__input) {
  color: v-bind('tokens.color.content.text.primary') !important;
}

:deep(.v-field--error .v-field__outline) {
  color: v-bind('tokens.color.input.border.error') !important;
}

:deep(.v-field--disabled) {
  background-color: v-bind('tokens.color.input.surface.disabled') !important;
  color: v-bind('tokens.color.input.text.disabled') !important;
  opacity: 1 !important;
}

:deep(.v-field--disabled .v-field__outline) {
  color: v-bind('tokens.color.input.border.disabled') !important;
  --v-field-border-opacity: 1 !important;
}

:deep(.v-input--disabled .v-label) {
  opacity: 1 !important;
  color: v-bind('tokens.color.input.text.disabled') !important;
}

:deep(.v-field--error > .v-field__field > .v-label),
:deep(.v-field--error > .v-field__outline > .v-field__outline__notch > .v-label) {
  color: v-bind('tokens.color.input.text.error') !important;
}

:deep(.v-messages__message) {
  display: flex;
  align-items: center;
  gap: 4px;
}

:deep(.v-field__input) {
  line-height: 24px;
  min-height: max(
    var(--v-input-control-height, 40px),
    1rem + var(--v-field-input-padding-top) + var(--v-field-input-padding-bottom) +
      var(--v-input-chips-margin-bottom, 0px) + 1px
  );
  padding-bottom: 4px;
  padding-top: 20px;
}

.input-message-icon {
  font-size: 12px;
}

:deep(.v-field__prepend-inner) {
  padding: 0 !important;

  &:has(.w-input-prefix) {
    padding-right: 8px !important;
  }
}

.w-input-prefix {
  white-space: nowrap;
  display: flex;
  align-items: center;
  height: 100%;
  color: v-bind('tokens.color.input.text.description');
  border-right: 1px solid v-bind('tokens.color.input.border.default');
  padding-right: 8px;
}

.no-label {
  :deep(.v-field__input) {
    padding-top: 8px !important;
    min-height: 40px !important;
  }
}

.w-input-description {
  margin-bottom: v-bind('tokens.spacing.gap.small');
}

.w-input-field--with-input-content {
  :deep(.v-field__input) {
    padding-inline: 0;
    padding-bottom: 0;
    row-gap: var(--spacing-gap-small);
  }

  :deep(.v-field__input > input) {
    flex-basis: 100%;
  }
}

.w-input-field--with-active-input-content {
  :deep(.v-field__input) {
    padding-top: 24px;
    padding-bottom: var(--spacing-inset-control-vertical-default);
  }
}

.w-input-field--embedded {
  width: 100%;

  :deep(.v-input) {
    min-width: 0;
    min-height: var(--type-lh-m) !important;
  }

  :deep(.v-field) {
    align-items: center;
    background-color: transparent !important;
    box-shadow: none !important;
    height: var(--type-lh-m) !important;
    min-height: var(--type-lh-m) !important;
    padding-inline: 0 !important;
  }

  :deep(.v-field__outline) {
    display: none !important;
  }

  :deep(.v-field--focused) {
    outline: none !important;
  }

  :deep(.v-field__field) {
    min-width: 0;
  }

  :deep(.v-field__input) {
    @include tag(label-regular-medium);

    height: var(--type-lh-m) !important;
    min-height: var(--type-lh-m) !important;
    padding: 0 !important;
    color: v-bind('tokens.color.input.text.value') !important;
  }

  :deep(.v-field__input::placeholder) {
    color: v-bind('tokens.color.input.text.placeholder') !important;
    opacity: 1;
  }

  :deep(.v-field__prepend-inner) {
    align-items: center;
    color: v-bind('tokens.color.input.icon.default') !important;
    height: 16px;
    padding-right: v-bind('tokens.spacing.gap.small') !important;
  }

  :deep(.w-input-field__search-icon) {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    margin-inline-end: 0 !important;
    color: v-bind('tokens.color.input.icon.default') !important;
  }
}
</style>
